import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
    MagnifyingGlass, Phone, MapPin, Flame, UserList, CheckCircle,
    UserCircle, Hand, UsersThree, ChatCircleDots, CalendarCheck, Question,
    Sparkle, EnvelopeSimple, ClockClockwise, SortAscending, Clock,
    Buildings, Warning, ArrowRight, Stethoscope, ArrowsClockwise,
    ShieldWarning, CaretDown,
} from "@phosphor-icons/react";
import { AdminKpiTile, CommandHeader } from "@/components/admin/AdminKpiTile";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";
import BackToBreadcrumb from "@/components/admin/BackToBreadcrumb";

// iter98.99em — Map each lead status to the next concrete action the
// PES should take. Renders inline below the patient name so the queue
// doubles as a to-do list, not just a roster.
const NEXT_ACTION_HINT = {
    new:            { label: "Call now",        cls: "text-rose-700",   icon: Phone },
    assigned:       { label: "Call now",        cls: "text-rose-700",   icon: Phone },
    contacted:      { label: "Awaiting reply",  cls: "text-violet-700", icon: ChatCircleDots },
    consulting:     { label: "Send proposal",   cls: "text-indigo-700", icon: ChatCircleDots },
    scan_scheduled: { label: "Confirm scan",    cls: "text-cyan-700",   icon: CalendarCheck },
    handed_off:     { label: "Done",            cls: "text-emerald-700",icon: CheckCircle },
    converted:      { label: "Done",            cls: "text-emerald-700",icon: CheckCircle },
    not_interested: { label: "Closed",          cls: "text-muted-foreground", icon: CheckCircle },
    invalid:        { label: "Closed",          cls: "text-muted-foreground", icon: CheckCircle },
};

// iter98.99em — Relative-time formatter for the "Last action" column.
// Returns "—" when missing, otherwise a compact age like "2h ago".
function relTime(iso) {
    if (!iso) return null;
    try {
        const t = new Date(iso).getTime();
        if (!Number.isFinite(t)) return null;
        const diffMs = Date.now() - t;
        if (diffMs < 60_000) return "just now";
        const mins = Math.floor(diffMs / 60_000);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(diffMs / 3_600_000);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(diffMs / 86_400_000);
        return `${days}d ago`;
    } catch { return null; }
}

function hoursSince(iso) {
    if (!iso) return null;
    try { return (Date.now() - new Date(iso).getTime()) / 3_600_000; }
    catch { return null; }
}

// iter98.99ej — How the lead landed in this PES's queue. Mirrors the
// `assignment_source` field stamped by leads_hub.py (admin assign) and
// pes_inbox.py (self-claim). Backfilled in `_enrich_lead_for_coe` for
// older rows so this map is exhaustive.
const ASSIGNMENT_SOURCE_PILLS = {
    admin: {
        label: "Admin",
        Icon: UserCircle,
        cls: "bg-slate-100 text-slate-700 border-slate-200",
        title: "Routed to you by an admin",
    },
    self_claim: {
        label: "Self-claim",
        Icon: Hand,
        cls: "bg-primary/10 text-primary border-primary/20",
        title: "You picked this up from the Shared Inbox",
    },
};

// iter98.99ek — Where the lead originated on the marketing site. Maps the
// `source` strings stamped by marketing.py / leads_hub.py / contact_form.py.
// iter98.99ew — B2B sources added so the unified My Queue can render
// direct-apply + B2B demo-request leads alongside web leads.
const LEAD_SOURCE_PILLS = {
    "smile-quiz":          { label: "Smile Quiz",   Icon: Sparkle,         cls: "bg-violet-100 text-violet-700 border-violet-200" },
    "smile-assessment":    { label: "Assessment",   Icon: Sparkle,         cls: "bg-violet-100 text-violet-700 border-violet-200" },
    "counsellor-callback": { label: "Callback",     Icon: Phone,           cls: "bg-amber-100 text-amber-800 border-amber-200" },
    "contact-form":        { label: "Contact Form", Icon: EnvelopeSimple,  cls: "bg-sky-100 text-sky-700 border-sky-200" },
    "direct-apply":        { label: "Direct Apply", Icon: Stethoscope,     cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    "b2b-demo-request":    { label: "Demo Request", Icon: Buildings,       cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
};

const STATUS_PILLS = {
    // iter98.99en — "Open" replaces "New" / "Assigned" so PES staff
    // have a single, action-oriented label for untouched rows. Both
    // map to the same amber pill so the column reads at a glance.
    new:            { label: "Open",           cls: "bg-amber-100 text-amber-800" },
    assigned:       { label: "Open",           cls: "bg-amber-100 text-amber-800" },
    contacted:      { label: "Contacted",      cls: "bg-violet-100 text-violet-700" },
    scan_scheduled: { label: "Scan scheduled", cls: "bg-cyan-100 text-cyan-700" },
    // iter98.99ew — "Referred to provider" replaces "Handed off". Maps
    // to the same emerald pill — handoff/referral is a positive
    // terminal state for the PES.
    handed_off:     { label: "Referred to provider", cls: "bg-emerald-100 text-emerald-700" },
    closed:         { label: "Closed",         cls: "bg-secondary text-muted-foreground" },
    // iter98.99eh — modern WEB_LEAD_STATUSES from leads_hub.py also render
    // with proper pills now (previously these collided with the default
    // "Assigned" fallback and looked wrong).
    consulting:     { label: "Consulting",     cls: "bg-indigo-100 text-indigo-700" },
    converted:      { label: "Converted",      cls: "bg-emerald-100 text-emerald-700" },
    not_interested: { label: "Not interested", cls: "bg-slate-100 text-slate-600" },
    invalid:        { label: "Invalid",        cls: "bg-rose-100 text-rose-700" },
    // iter98.99ew — B2B-specific lead statuses surfaced inside the
    // unified My Queue table. Mirrors the labels used in
    // /coe/B2BLeadsList so the same pill semantics carry through.
    discovery_done:    { label: "Discovery",      cls: "bg-violet-100 text-violet-700" },
    qualified:         { label: "Qualified",      cls: "bg-indigo-100 text-indigo-700" },
    application_sent:  { label: "App. sent",      cls: "bg-amber-100 text-amber-800" },
    applied:           { label: "Applied",        cls: "bg-emerald-100 text-emerald-700" },
    handed_to_admin:   { label: "Admin review",   cls: "bg-fuchsia-100 text-fuchsia-700" },
    won:               { label: "Won",            cls: "bg-emerald-200 text-emerald-800" },
    lost:              { label: "Lost",           cls: "bg-rose-100 text-rose-700" },
};

function scoreTier(score) {
    if (score >= 70) return { label: "Hot", cls: "bg-rose-100 text-rose-700 border-rose-200" };
    if (score >= 45) return { label: "Warm", cls: "bg-amber-100 text-amber-700 border-amber-200" };
    if (score > 0) return { label: "Cool", cls: "bg-sky-100 text-sky-700 border-sky-200" };
    return { label: "Cold", cls: "bg-secondary text-muted-foreground border-border" };
}

// iter98.99ek — Filter chip / stat-card definitions. Each entry computes
// its count from the in-memory leads array and applies a predicate to the
// filtered list. Clicking a card OR a chip applies the same filter so the
// two surfaces stay in lockstep.
const QUICK_FILTERS = [
    {
        key: "all",
        label: "All",
        caption: "On your queue",
        Icon: UsersThree,
        tone: "primary",
        match: () => true,
    },
    {
        key: "hot",
        label: "Hot",
        caption: "Score ≥ 70",
        Icon: Flame,
        tone: "rose",
        match: (l) => (l.score || 0) >= 70,
    },
    {
        key: "needs_touch",
        label: "Open",
        caption: "First action needed",
        Icon: Phone,
        tone: "amber",
        match: (l) => ["new", "assigned"].includes(l.lead_status),
    },
    {
        key: "in_convo",
        label: "Follow-up",
        caption: "Awaiting reply",
        Icon: ChatCircleDots,
        tone: "violet",
        match: (l) => l.lead_status === "contacted",
    },
    // iter98.99ew — Missed callback window. Patient picked a specific
    // call slot, the slot has passed, and we never made first contact.
    // Drives a rose stat card + red badge per row so PES surfaces these
    // ahead of generic open leads.
    {
        key: "missed_callback",
        label: "Missed callback",
        caption: "Window passed",
        Icon: Warning,
        tone: "rose",
        match: (l) => l.missed_callback === true,
    },
    // iter101.38 — Hot & untouched: score ≥ 70 and no first-touch yet.
    // The PES dashboard's "Web leads · Hot & untouched" tile deep-links
    // here so clicking through lands on exactly this pre-filter.
    // Predicate mirrors pes-home's classifier (line ~179 in pes_home.py).
    {
        key: "hot_untouched",
        label: "Hot & untouched",
        caption: "Score ≥ 70, no reply",
        Icon: Flame,
        tone: "amber",
        match: (l) =>
            (l.score || 0) >= 70 &&
            !l.hub_first_touch_at &&
            ["new", "assigned"].includes(l.lead_status),
    },
    // iter101.38 — SLA at risk: under 60 min from the 24h auto-release
    // deadline (backend line ~172). Predicate uses hours since claim so
    // the number stays accurate even if the client-side clock drifts.
    {
        key: "sla_risk",
        label: "SLA at risk",
        caption: "Under 60 min to breach",
        Icon: ShieldWarning,
        tone: "amber",
        match: (l) => {
            if (l.hub_first_touch_at) return false;
            const claimed = l.hub_assigned_at || l.created_at;
            if (!claimed) return false;
            const claimHours = (Date.now() - new Date(claimed).getTime()) / 3_600_000;
            return claimHours >= 23 && claimHours < 24;
        },
    },
    // iter98.99ew — "Scan booked" card removed at user request. The
    // status still exists in the data model but no longer surfaces
    // as a top-level filter; PES action focus stays on Open → Follow-up
    // → Referred to Provider.
    {
        key: "handed_off",
        label: "Referred to Provider",
        caption: "Won",
        Icon: CheckCircle,
        tone: "emerald",
        match: (l) => l.lead_status === "handed_off",
    },
    // iter98.99ew — Kind filter cards. "Web Leads" and "B2B Leads" sit
    // at the end of the strip so the PES can pivot the same unified
    // queue between patients (Smile Quiz, counsellor callbacks) and
    // providers (direct-apply, B2B demo requests).
    {
        key: "kind_web",
        label: "Web Leads",
        caption: "Patients",
        Icon: UserCircle,
        tone: "cyan",
        match: (l) => l.kind === "web",
    },
    {
        key: "kind_b2b",
        label: "B2B Leads",
        caption: "Providers",
        Icon: Buildings,
        tone: "violet",
        match: (l) => l.kind === "b2b",
    },
];

// iter98.99ek — Card-shaped tile inspired by the user's reference design.
// Tinted icon-circle on the left, uppercase label, big number, caption.
// Clickable so each card doubles as a quick filter.
const STAT_TONE_CLS = {
    primary: { ring: "bg-primary/10 text-primary",   text: "text-primary",   activeBorder: "border-primary",   activeBg: "bg-primary/[0.04]" },
    rose:    { ring: "bg-rose-100 text-rose-600",    text: "text-rose-600",  activeBorder: "border-rose-300",  activeBg: "bg-rose-50/60" },
    amber:   { ring: "bg-amber-100 text-amber-700",  text: "text-amber-700", activeBorder: "border-amber-300", activeBg: "bg-amber-50/60" },
    violet:  { ring: "bg-violet-100 text-violet-700",text: "text-violet-700",activeBorder: "border-violet-300",activeBg: "bg-violet-50/60" },
    cyan:    { ring: "bg-cyan-100 text-cyan-700",    text: "text-cyan-700",  activeBorder: "border-cyan-300",  activeBg: "bg-cyan-50/60" },
    emerald: { ring: "bg-emerald-100 text-emerald-700", text: "text-emerald-700", activeBorder: "border-emerald-300", activeBg: "bg-emerald-50/60" },
};

function StatCard({ entry, value, active, onClick }) {
    const tone = STAT_TONE_CLS[entry.tone] || STAT_TONE_CLS.primary;
    const Icon = entry.Icon;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left rounded-2xl bg-card border transition-all px-2.5 py-3 hover:shadow-md ${
                active ? `${tone.activeBorder} ${tone.activeBg} shadow-sm` : "border-border"
            }`}
            data-testid={`coe-leads-stat-${entry.key}`}
            aria-pressed={active}
        >
            {/* iter98.99ew — Vertical layout: icon + label sit on the
                top row but the icon shrinks to leave room so labels
                like "Referred to Provider" don't wrap 3-deep. */}
            <div className="flex items-center gap-1.5">
                <span className={`w-6 h-6 rounded-full grid place-items-center ${tone.ring} shrink-0`}>
                    <Icon size={11} weight="duotone" />
                </span>
                <div className="text-[8.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground leading-tight">
                    {entry.label}
                </div>
            </div>
            <div className={`mt-2 font-display text-[22px] font-bold tabular-nums leading-none ${tone.text}`}>
                {value}
            </div>
            <div className="text-[9.5px] text-muted-foreground mt-1 truncate">{entry.caption}</div>
        </button>
    );
}


export default function CoeLeadsList({ embedded = false }) {
    /* iter101.38 — Filter is URL-synced (`?filter=`) so the PES dashboard's
       Daily Focus card rows can deep-link straight into the pre-filtered
       queue. Also makes the URL shareable — a PES can bookmark "SLA at
       risk" as a live query. */
    const [searchParams, setSearchParams] = useSearchParams();
    const validFilterKey = (k) => k && QUICK_FILTERS.some((f) => f.key === k);
    const initialFilter = validFilterKey(searchParams.get("filter"))
        ? searchParams.get("filter")
        : "all";

    const [leads, setLeads] = useState([]);
    const [q, setQ] = useState("");
    const [activeFilter, setActiveFilterState] = useState(initialFilter);

    /* Keep the URL in sync when the operator clicks a chip. `all` clears
       the param so the base URL stays clean. */
    const setActiveFilter = (key) => {
        setActiveFilterState(key);
        const next = new URLSearchParams(searchParams);
        if (key === "all") next.delete("filter");
        else next.set("filter", key);
        setSearchParams(next, { replace: true });
    };

    /* React to browser back/forward and to deep-link changes made by
       parent surfaces (e.g. the PES dashboard rewriting `?filter=`). */
    useEffect(() => {
        const p = searchParams.get("filter");
        const next = validFilterKey(p) ? p : "all";
        if (next !== activeFilter) setActiveFilterState(next);
    }, [searchParams, activeFilter]);
    // iter98.99em — Sort the table. Default = oldest-touched first so
    // dormant rows surface to the top (drives follow-ups). "score" =
    // hottest first when triaging a fresh batch.
    const [sortKey, setSortKey] = useState("last_action_asc");
    const [loading, setLoading] = useState(true);

    // iter98.99ew — Normalize web + b2b into a single shape so the
    // queue table can render both kinds without branching. Web leads
    // already match `_enrich_lead_for_coe`. B2B leads need
    // (clinic_name → name, contact_name → subtitle, status →
    // lead_status, updated_at → last_action_at) coercions.
    const normalizeWeb = (l) => ({ ...l, kind: "web" });
    const normalizeB2b = (b) => ({
        kind: "b2b",
        id: b.id,
        code: b.code,
        // The display "name" is the clinic, with the contact line under it.
        name: b.clinic || b.contact_name || "Clinic",
        contact_name: b.contact_name,
        phone: b.phone,
        city: b.city,
        // Map B2B status onto the same lead_status pill the UI already
        // understands. "applied" / "new" / etc. are b2b-specific so we
        // fall back to a neutral pill via STATUS_PILLS when unknown.
        lead_status: b.status,
        // No score on B2B — render "—" downstream.
        score: null,
        // Surface source for the source pill (direct-apply vs demo).
        source: b.source,
        // Reuse "self-claim" for the assignment-source pill so admins
        // still recognise where the lead came from.
        assignment_source: b.hub_assignee_id ? "self-claim" : "admin",
        last_action_at: b.updated_at || b.created_at,
        last_action_label: "Updated",
        // B2B rows aren't subject to the missed-callback flag (those
        // are patient-initiated time slots), so always false.
        missed_callback: false,
        preferred_slot: "",
        partner_doctor_name: null,
    });

    useEffect(() => {
        reload();
    }, []);

    // iter98.99ew — Manual reload bound to the new Refresh button + the
    // initial mount. Pulls both kinds in parallel and merges into the
    // single `leads` array (web + b2b normalized via the helpers above).
    function reload() {
        setLoading(true);
        Promise.all([
            api.get("/coe/leads"),
            api.get("/coe/b2b-leads").catch(() => ({ data: [] })),
        ])
            .then(([w, b]) => {
                const merged = [
                    ...(w.data || []).map(normalizeWeb),
                    ...(b.data || []).map(normalizeB2b),
                ];
                setLeads(merged);
            })
            .finally(() => setLoading(false));
    }

    // Counts per quick-filter — computed from the full leads array so the
    // numbers stay stable regardless of the search/filter state below.
    const counts = useMemo(() => {
        const out = {};
        for (const f of QUICK_FILTERS) {
            out[f.key] = leads.filter(f.match).length;
        }
        return out;
    }, [leads]);

    const filtered = useMemo(() => {
        const activeMatch = (QUICK_FILTERS.find((f) => f.key === activeFilter) || QUICK_FILTERS[0]).match;
        const low = q.toLowerCase();
        const rows = leads.filter((l) => {
            if (!activeMatch(l)) return false;
            if (!q) return true;
            return (
                (l.name || "").toLowerCase().includes(low) ||
                (l.phone || "").includes(q) ||
                (l.city || "").toLowerCase().includes(low) ||
                // iter98.99eo — Lead ID is searchable so PES can paste a
                // code (e.g. "IVQ-LD-2627-0007") into the search bar.
                (l.code || "").toLowerCase().includes(low)
            );
        });
        // Sort. last_action_asc = oldest touch first (drives follow-up);
        // score_desc = hottest first.
        if (sortKey === "score_desc") {
            rows.sort((a, b) => (b.score || 0) - (a.score || 0));
        } else {
            rows.sort((a, b) => {
                const ta = a.last_action_at ? new Date(a.last_action_at).getTime() : 0;
                const tb = b.last_action_at ? new Date(b.last_action_at).getTime() : 0;
                return ta - tb;
            });
        }
        return rows;
    }, [leads, q, activeFilter, sortKey]);

    return (
        <div data-testid="coe-leads-list" className="space-y-6">
            {!embedded && (
                <>
                    <BackToBreadcrumb />
                    <CommandHeader
                        overline="Patient Engagement · My Queue"
                        title="My Queue"
                        subtitle={`${leads.length} item${leads.length === 1 ? "" : "s"} you own — assigned by admin or claimed from the Shared Inbox.`}
                        testid="coe-leads-header"
                        right={
                            <button
                                type="button"
                                onClick={reload}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-sm hover:bg-secondary/40 transition"
                                data-testid="coe-leads-refresh"
                            >
                                <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh
                            </button>
                        }
                    />

                    {/* iter101.40 — 6-tile KPI strip (Admin Command Center grammar).
                        Each tile drives the same `activeFilter` state as the
                        FilterChipRow beneath, so the two surfaces stay in sync.
                        Picks the six most operator-critical focuses; the
                        remaining filters live in the chip row (overflowable). */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="coe-leads-kpi-strip">
                        <AdminKpiTile label="All open"          value={counts.all ?? 0}             tone="primary" Icon={UsersThree}   active={activeFilter === "all"}             onClick={() => setActiveFilter("all")}             testid="coe-leads-kpi-all" />
                        <AdminKpiTile label="Hot & untouched"   value={counts.hot_untouched ?? 0}   tone="rose"    Icon={Flame}        active={activeFilter === "hot_untouched"}   onClick={() => setActiveFilter("hot_untouched")}   testid="coe-leads-kpi-hot-untouched" />
                        <AdminKpiTile label="SLA at risk"       value={counts.sla_risk ?? 0}        tone="amber"   Icon={ShieldWarning} active={activeFilter === "sla_risk"}       onClick={() => setActiveFilter("sla_risk")}        testid="coe-leads-kpi-sla-risk" />
                        <AdminKpiTile label="Missed callback"   value={counts.missed_callback ?? 0} tone="rose"    Icon={Warning}      active={activeFilter === "missed_callback"} onClick={() => setActiveFilter("missed_callback")} testid="coe-leads-kpi-missed-callback" />
                        <AdminKpiTile label="Open"              value={counts.needs_touch ?? 0}     tone="amber"   Icon={Phone}        active={activeFilter === "needs_touch"}     onClick={() => setActiveFilter("needs_touch")}     testid="coe-leads-kpi-needs-touch" />
                        <AdminKpiTile label="Follow-up"         value={counts.in_convo ?? 0}        tone="violet"  Icon={ChatCircleDots} active={activeFilter === "in_convo"}      onClick={() => setActiveFilter("in_convo")}        testid="coe-leads-kpi-in-convo" />
                    </div>

                    {/* iter101.40 — Kind-axis segmented tabs (same primitive as
                        /admin/leads). Sets `activeFilter` to the matching kind
                        or terminal state so the row filter picks it up. */}
                    <AdminSegmentedTabs
                        tabs={[
                            { id: "all",        label: "All",          Icon: UsersThree,  count: counts.all ?? 0 },
                            { id: "kind_web",   label: "Web",          Icon: UserCircle,  count: counts.kind_web ?? 0 },
                            { id: "kind_b2b",   label: "B2B",          Icon: Buildings,   count: counts.kind_b2b ?? 0 },
                            { id: "handed_off", label: "Referred",     Icon: CheckCircle, count: counts.handed_off ?? 0 },
                        ]}
                        active={["all", "kind_web", "kind_b2b", "handed_off"].includes(activeFilter) ? activeFilter : "all"}
                        onChange={setActiveFilter}
                        testid="coe-leads-tabs"
                        tabTestidBase="coe-leads-tab"
                        showZero
                    />

                    {/* iter101.40 — FilterSearchBar with sort dropdown in the
                        right slot. Same shape as /admin/leads so PES staff
                        recognise the shortcuts across the two surfaces. */}
                    <FilterSearchBar
                        query={q}
                        setQuery={setQ}
                        placeholder="Search name, phone, city, or Lead ID…"
                        searchTestid="coe-leads-search"
                        testid="coe-leads-search-bar"
                        rightSlot={
                            <div className="relative">
                                <select
                                    value={sortKey}
                                    onChange={(e) => setSortKey(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                    data-testid="coe-leads-sort-select"
                                    aria-label="Sort my queue"
                                >
                                    <option value="last_action_asc">Oldest touch first (default)</option>
                                    <option value="score_desc">Hottest first</option>
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                        }
                    />

                    {/* iter101.40 — Extra focus chips (the ones that didn't
                        make the 6-tile cut). Same FilterChipRow used by
                        /admin/leads so the vocabulary carries over. */}
                    <FilterChipRow
                        chips={[
                            { id: "hot",         label: "Hot",             count: counts.hot ?? 0,        Icon: Flame,        tone: "rose"    },
                        ]}
                        value={["hot"].includes(activeFilter) ? activeFilter : null}
                        onChange={(id) => setActiveFilter(id ?? "all")}
                        testid="coe-leads-chip-row"
                    />
                </>
            )}

            {loading && <div className="text-muted-foreground">Loading…</div>}

            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground">
                    <UserList size={32} className="mx-auto mb-2 opacity-50" />
                    {leads.length === 0
                        ? "Your queue is empty. Pick something up from the Shared Inbox, or wait for admin to route work to you."
                        : (q
                            ? "No leads match your search."
                            : `No leads in "${(QUICK_FILTERS.find((f) => f.key === activeFilter) || {}).label}" right now.`)}
                </div>
            )}

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {/* iter98.99em — Grid: Patient (3) · Source (2) · Status + Next-action hint (2)
                    · Score (1) · Assigned by (2) · Last action (2) = 12 cols.
                    Dropped the Phone column — it's now a subtle line under
                    the patient name so we can add the high-value
                    "Last action" + "Next action" cues without overcrowding. */}
                {filtered.length > 0 && (
                    <div
                        className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-secondary/30 border-b border-border text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground"
                        data-testid="coe-leads-header"
                    >
                        <div className="col-span-3">Patient</div>
                        <div className="col-span-2">Source</div>
                        <div className="col-span-2">Status / Next</div>
                        <div className="col-span-1">Score</div>
                        <div className="col-span-2">Assigned by</div>
                        <div className="col-span-2">Last action</div>
                    </div>
                )}
                {filtered.map((l) => {
                    const isB2B = l.kind === "b2b";
                    const tier = scoreTier(l.score || 0);
                    const status = STATUS_PILLS[l.lead_status] || STATUS_PILLS.assigned;
                    const source = ASSIGNMENT_SOURCE_PILLS[l.assignment_source];
                    const leadSrc = LEAD_SOURCE_PILLS[l.source] || {
                        label: l.source || "Unknown",
                        Icon: Question,
                        cls: "bg-secondary text-muted-foreground border-border",
                    };
                    const nextAction = NEXT_ACTION_HINT[l.lead_status] || NEXT_ACTION_HINT.new;
                    const NextIcon = nextAction.icon;
                    const lastAge = relTime(l.last_action_at);
                    const lastHrs = hoursSince(l.last_action_at);
                    const isStale = lastHrs != null && lastHrs >= 24;
                    return (
                        <Link
                            to={isB2B ? `/coe/b2b-leads/${l.id}` : `/coe/leads/${l.id}`}
                            key={`${l.kind}-${l.id}`}
                            className={`grid grid-cols-12 gap-3 px-5 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40 transition-colors items-center ${
                                l.missed_callback ? "bg-rose-50/40" : ""
                            }`}
                            data-testid={`coe-lead-${l.id}`}
                        >
                            <div className="col-span-12 md:col-span-3 min-w-0">
                                <div className="font-medium text-sm truncate flex items-center gap-1.5">
                                    {/* iter98.99ew — Kind badge so PES can tell
                                        a Web (patient) row apart from a B2B
                                        (provider) row at a glance in the
                                        unified table. */}
                                    <span
                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider border ${
                                            isB2B
                                                ? "bg-violet-100 text-violet-700 border-violet-200"
                                                : "bg-cyan-100 text-cyan-700 border-cyan-200"
                                        }`}
                                        data-testid={`coe-lead-kind-${l.id}`}
                                    >
                                        {isB2B ? <Buildings size={9} weight="fill" /> : <UserCircle size={9} weight="fill" />}
                                        {isB2B ? "B2B" : "Web"}
                                    </span>
                                    <span className="truncate">{l.name}</span>
                                    {/* iter98.99ew — Per-row missed-callback badge. */}
                                    {l.missed_callback && (
                                        <span
                                            className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full"
                                            data-testid={`coe-lead-missed-${l.id}`}
                                            title="Patient picked a call slot we missed"
                                        >
                                            <Warning size={9} weight="fill" /> Missed
                                        </span>
                                    )}
                                </div>
                                {/* iter98.99eo — Lead ID in mono just under the name. */}
                                {l.code && (
                                    <div className="text-[10px] font-mono text-muted-foreground/70 truncate mt-0.5" data-testid={`coe-lead-code-${l.id}`}>
                                        {l.code}
                                    </div>
                                )}
                                <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                    <Phone size={11} className="shrink-0" />
                                    <span className="truncate">{l.phone}</span>
                                    {l.city && (
                                        <>
                                            <span aria-hidden>·</span>
                                            <MapPin size={11} className="shrink-0" />
                                            <span className="truncate">{l.city}</span>
                                        </>
                                    )}
                                </div>
                                {/* iter98.99er — Preferred call-time chip when the
                                    caller picked one during the Smile-Quiz
                                    post-submit step. Compact amber chip so the
                                    PES can spot the right call window from
                                    the queue without opening the row. */}
                                {l.preferred_slot && (
                                    <div
                                        className="text-[10px] font-semibold text-amber-700 inline-flex items-center gap-1 mt-1 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 max-w-full"
                                        title={`Caller asked for ${l.preferred_slot}`}
                                        data-testid={`coe-lead-slot-${l.id}`}
                                    >
                                        <Clock size={10} weight="fill" className="shrink-0" />
                                        <span className="truncate">{l.preferred_slot}</span>
                                    </div>
                                )}
                            </div>
                            <div className="col-span-6 md:col-span-2" data-testid={`coe-lead-leadsrc-${l.id}`}>
                                <span className={`text-[10px] px-2 py-1 rounded-full border uppercase font-semibold inline-flex items-center gap-1 ${leadSrc.cls}`}>
                                    <leadSrc.Icon size={11} weight="fill" />
                                    {leadSrc.label}
                                </span>
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                <span className={`text-[10px] px-2 py-1 rounded-full uppercase font-semibold inline-flex items-center gap-1 ${status.cls}`}>
                                    {l.lead_status === "handed_off" && <CheckCircle size={10} weight="fill" />}
                                    {status.label}
                                </span>
                                <div className={`text-[10.5px] mt-1 flex items-center gap-1 font-semibold ${nextAction.cls}`} data-testid={`coe-lead-next-${l.id}`}>
                                    <NextIcon size={10} weight="fill" />
                                    {nextAction.label}
                                </div>
                            </div>
                            <div className="col-span-4 md:col-span-1">
                                {/* iter98.99ew — B2B rows have no patient
                                    score; render an em-dash so the column
                                    stays aligned without a misleading "Cold 0". */}
                                {isB2B ? (
                                    <span className="text-[11px] text-muted-foreground tabular-nums">—</span>
                                ) : (
                                    <span className={`text-[10px] px-2 py-1 rounded-full border uppercase font-semibold inline-flex items-center gap-1 ${tier.cls}`}>
                                        {tier.label === "Hot" && <Flame size={10} weight="fill" />}
                                        {tier.label} {l.score || 0}
                                    </span>
                                )}
                            </div>
                            <div className="col-span-8 md:col-span-2 md:text-left" data-testid={`coe-lead-source-${l.id}`}>
                                {source ? (
                                    <span
                                        className={`text-[10px] px-2 py-1 rounded-full uppercase font-semibold inline-flex items-center gap-1 border ${source.cls}`}
                                        title={source.title}
                                    >
                                        <source.Icon size={11} weight="fill" />
                                        {source.label}
                                    </span>
                                ) : (
                                    <span className="text-[10px] text-muted-foreground italic">—</span>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-2" data-testid={`coe-lead-last-action-${l.id}`}>
                                {lastAge ? (
                                    <div className={`text-[11.5px] font-semibold tabular-nums inline-flex items-center gap-1 ${isStale ? "text-rose-700" : "text-muted-foreground"}`}>
                                        <ClockClockwise size={11} weight={isStale ? "fill" : "duotone"} />
                                        {lastAge}
                                    </div>
                                ) : (
                                    <div className="text-[11px] text-muted-foreground italic">—</div>
                                )}
                                {l.last_action_label && (
                                    <div className="text-[10px] text-muted-foreground/80 mt-0.5 truncate">
                                        {l.last_action_label}
                                    </div>
                                )}
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
